// Ports design/hub/login.html's inline Authentik Flow Executor client (lines
// ~275-398 there) into a typed module usable from a React component. The
// protocol/quirks are unchanged from the hub's version — see
// docs/deployment.md's "Custom login UI (Flow Executor API)" section for the
// full narrative of *why* each of these steps exists (CORS, the Location
// rewrite, conditional CSRF enforcement, the final_redirect distinction, the
// `next` wrapper key, and the cookie-domain fix). This module only replicates
// the mechanics; it does not re-derive them.
//
// Caddy must proxy this app's own `/idp/*` to Authentik the same way
// hub.abrarfarms.com does (see infra/caddy/Caddyfile's maps.abrarfarms.com
// block) — the flow executor API has no CORS support at all, so every call
// here must be same-origin.

import { authorizeUrl } from '../config/env';

export type FlowStage =
  | { component: 'ak-stage-identification'; response_errors?: FieldErrors }
  | { component: 'ak-stage-password'; response_errors?: FieldErrors }
  | { component: 'xak-flow-redirect'; to?: string; final_redirect?: boolean }
  | { component: string; response_errors?: FieldErrors; [key: string]: unknown };

type FieldErrors = Record<string, Array<{ string?: string }>>;

export class FlowError extends Error {}

function buildFlowUrl(): string {
  // Authentik's flow-completion logic (_flow_done() in
  // authentik/flows/views/executor.py) reads its redirect target from a
  // `next` key *inside* the flow executor's own `query=` string, not from
  // the OAuth params directly — see docs/deployment.md for the bug this
  // caused when that wasn't understood. `next` must be relative.
  const authUrlObj = new URL(authorizeUrl());
  const relativeNext = authUrlObj.pathname + authUrlObj.search;
  return (
    '/idp/api/v3/flows/executor/default-authentication-flow/?query=' +
    encodeURIComponent('next=' + encodeURIComponent(relativeNext))
  );
}

// Non-HttpOnly, readable via document.cookie — required only once a session
// cookie already exists (Authentik's DRF SessionAuthentication only enforces
// CSRF for already-authenticated sessions; anonymous visitors are exempt).
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)authentik_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function call(flowUrl: string, body: Record<string, unknown>): Promise<FlowStage> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const token = csrfToken();
  if (token) headers['X-Authentik-CSRF'] = token;
  const res = await fetch(flowUrl, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
  return res.json();
}

async function fetchStage(flowUrl: string): Promise<FlowStage> {
  const res = await fetch(flowUrl, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return res.json();
}

function fieldError(stage: FlowStage, field: string, fallback: string): FlowError {
  const errs = (stage as { response_errors?: FieldErrors }).response_errors?.[field];
  return new FlowError(errs && errs[0]?.string ? errs[0].string! : fallback);
}

export interface Credentials {
  email: string;
  password: string;
}

/** Result of a completed flow: the browser should navigate here to finish
 * the OAuth dance and land back in the app. */
export interface FlowRedirect {
  kind: 'redirect';
  url: string;
}

/** Signals a stage this client doesn't have custom UI for (e.g. MFA) — the
 * caller should send the browser to Authentik's own hosted flow page. */
export interface FlowFallback {
  kind: 'fallback';
  url: string;
}

export type FlowOutcome = FlowRedirect | FlowFallback;

/**
 * Walks the Authentik Flow Executor state machine forward from whatever
 * stage it hands back, using the caller-supplied credentials for the two
 * stages this client knows how to answer. Any other stage falls back to
 * Authentik's own hosted page (mirrors login.html's behavior exactly).
 */
export async function runLoginFlow(creds: Credentials): Promise<FlowOutcome> {
  const flowUrl = buildFlowUrl();
  return advanceFlow(flowUrl, await fetchStage(flowUrl), creds);
}

// NOTE: the former checkExistingSession() — which probed the LOGIN FLOW to
// decide whether a session existed — was removed. It treated every first
// response other than a final redirect (intermediate redirects, stale
// half-finished flow plans, network errors) as "not logged in", which was
// the intermittent refresh-to-login bug. Session detection now lives in
// session.ts (Authentik's users/me endpoint); this module is only the
// actual credential login.

async function advanceFlow(
  flowUrl: string,
  initialStage: FlowStage,
  creds: Credentials,
): Promise<FlowOutcome> {
  async function advance(stage: FlowStage): Promise<FlowOutcome> {
    if (stage.component === 'xak-flow-redirect') {
      const s = stage as Extract<FlowStage, { component: 'xak-flow-redirect' }>;
      if (!s.final_redirect) {
        // Intermediate redirect (e.g. auto-skipping an unconfigured MFA
        // stage) — Authentik's `to` is a bare path relative to its own
        // root, unaware it's served under /idp/ here. Re-poll our own
        // known-good flow URL instead of following it.
        return advance(await fetchStage(flowUrl));
      }
      // Final redirect out of the flow, back into the OAuth dance. Stay on
      // our own origin (via /idp/) so the authentik_session cookie set
      // through this proxy is actually sent — see docs/deployment.md for
      // why navigating straight to auth.abrarfarms.com drops it.
      return { kind: 'redirect', url: window.location.origin + '/idp' + s.to };
    }

    if (stage.component === 'ak-stage-identification') {
      if (stage.response_errors) {
        throw fieldError(stage, 'uid_field', "That email address isn't recognized.");
      }
      return advance(await call(flowUrl, { uid_field: creds.email.trim() }));
    }

    if (stage.component === 'ak-stage-password') {
      if (stage.response_errors) {
        throw fieldError(stage, 'password', 'Incorrect password.');
      }
      return advance(await call(flowUrl, { password: creds.password }));
    }

    // Some other stage with no custom UI here (e.g. MFA) — hand off to
    // Authentik's own hosted page.
    const fallback = new URL(authorizeUrl());
    if (creds.email.trim()) fallback.searchParams.set('login_hint', creds.email.trim());
    return { kind: 'fallback', url: fallback.toString() };
  }

  return advance(initialStage);
}
